<template>
     <div class="booking-success-view">
       <div class="container">
         <div class="success-card">
           <div class="success-icon">
             <span>&#10004;</span>
           </div>
           <h1 class="success-title">{{ isEnquiry ? 'Enquiry Submitted!' : 'Appointment Booked!' }}</h1>
           <p class="success-subtitle">
             {{ isEnquiry
               ? 'Thank you for your interest in our academy. Our team will contact you soon with course details.'
               : 'Thank you for scheduling your appointment with us. We look forward to seeing you!' }}
           </p>
           
           <div v-if="loading" class="loading-spinner"></div>
   
           <div v-if="error" class="error-message">
             <p>{{ error }}</p>
           </div>
   
           <div v-if="appointment" class="appointment-details">
             <h2 class="details-title">{{ isEnquiry ? 'Your Enquiry Details' : 'Your Appointment Details' }}</h2>
             <div class="detail-item">
               <strong>Appointment ID:</strong>
               <span>{{ appointment.id }}</span>
             </div>
             <div class="detail-item">
               <strong>Date:</strong>
               <span>{{ formatDate(appointment.appointment_date) }}</span>
             </div>
             <div class="detail-item">
               <strong>Status:</strong>
               <span class="status-pill" :class="`status-${appointment.status}`">{{ appointment.status }}</span>
             </div>
             <div v-if="appointment.customer" class="detail-item">
               <strong>Name:</strong>
               <span>{{ appointment.customer.firstName }} {{ appointment.customer.lastName }}</span>
             </div>
             <div v-if="appointment.services && appointment.services.length" class="detail-item">
               <strong>Services:</strong>
               <ul>
                 <li v-for="service in appointment.services" :key="service.id">
                   {{ service.service ? service.service.name : 'N/A' }}
                 </li>
               </ul>
             </div>
           </div>
   
           <router-link to="/" class="btn btn-primary">Back to Home</router-link>
         </div>
       </div>
     </div>
</template>
   
<script lang="ts">
   import { defineComponent, ref, onMounted, computed } from 'vue';
   import { useRoute } from 'vue-router';
   import appointmentService from '@/services/appointment.service';
   import { IAppointment } from '@/types/appointment.types';
   
   export default defineComponent({
     name: 'BookingSuccessView',
     setup() {
       const route = useRoute();
       const appointment = ref<IAppointment | null>(null);
       const loading = ref(true);
       const error = ref<string | null>(null);
       const isEnquiry = computed(() => route.query.type === 'enquiry');
   
       const fetchAppointmentDetails = async (id: string) => {
         try {
           loading.value = true;
           const stateAppointment = history.state?.appointment;
           if (stateAppointment && stateAppointment.id === id) {
             appointment.value = stateAppointment;
             return;
           }
           const viewToken = route.query.token as string | undefined;
           appointment.value = await appointmentService.getPublicAppointmentById(id, viewToken);
         } catch (err) {
           error.value = 'Failed to load appointment details. Please contact us for assistance.';
         } finally {
           loading.value = false;
         }
       };
   
       const formatDate = (dateString: string) => {
         if (!dateString) return 'N/A';
         const options: Intl.DateTimeFormatOptions = {
           year: 'numeric',
           month: 'long',
           day: 'numeric',
         };
         return new Date(dateString).toLocaleDateString(undefined, options);
       };
   
       onMounted(() => {
         const appointmentId = route.params.id as string;
         if (appointmentId) {
           fetchAppointmentDetails(appointmentId);
         } else {
           error.value = 'No appointment ID provided.';
           loading.value = false;
         }
       });
   
       return {
         appointment,
         loading,
         error,
         isEnquiry,
         formatDate,
       };
     },
   });
</script>
   
<style scoped>
   .booking-success-view {
     padding: 80px 20px;
     background-color: #f0fdf4; /* Light green background */
     min-height: 100vh;
     display: flex;
     align-items: center;
     justify-content: center;
   }
   
   .container {
     max-width: 700px;
     width: 100%;
     margin: 0 auto;
     text-align: center;
   }
   
   .success-card {
     background-color: #fff;
     padding: 50px;
     border-radius: 16px;
     box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
     border-top: 5px solid #22c55e; /* Green accent */
   }
   
   .success-icon {
     width: 80px;
     height: 80px;
     background-color: #22c55e;
     color: white;
     border-radius: 50%;
     display: flex;
     align-items: center;
     justify-content: center;
     font-size: 2.5em;
     margin: 0 auto 20px auto;
   }
   
   .success-title {
     font-size: 2.5em;
     font-weight: 700;
     color: var(--brand-black, #333);
     margin-bottom: 0.4em;
   }
   
   .success-subtitle {
     font-size: 1.15em;
     color: #555;
     margin-bottom: 2.5em;
   }
   
   .appointment-details {
     margin-bottom: 2.5em;
     padding: 20px;
     background-color: #f9fafb;
     border-radius: 8px;
     border: 1px solid #e5e7eb;
     text-align: left;
   }
   
   .details-title {
     font-size: 1.5em;
     font-weight: 600;
     color: var(--brand-black);
     margin-bottom: 1em;
     border-bottom: 1px solid #e5e7eb;
     padding-bottom: 0.5em;
   }
   
   .detail-item {
     display: flex;
     justify-content: space-between;
     padding: 10px 0;
     font-size: 1.05em;
   }
   .detail-item:not(:last-child) {
     border-bottom: 1px solid #f3f4f6;
   }
   .detail-item strong {
     color: #374151;
     margin-right: 15px;
   }
   .detail-item span {
     color: #4b5563;
   }
   .detail-item ul {
     list-style: none;
     padding: 0;
     margin: 0;
     text-align: right;
   }
   
   .status-pill {
     padding: 4px 12px;
     border-radius: 16px;
     font-weight: 600;
     font-size: 0.9em;
     text-transform: capitalize;
     color: #fff;
   }
   .status-booked { background-color: #3b82f6; }
   .status-confirmed { background-color: #16a34a; }
   .status-completed { background-color: #4f46e5; }
   .status-cancelled { background-color: #ef4444; }
   .status-no-show { background-color: #71717a; }
   
   .loading-spinner {
     border: 4px solid #f3f3f3;
     border-top: 4px solid var(--brand-gold);
     border-radius: 50%;
     width: 40px;
     height: 40px;
     animation: spin 1s linear infinite;
     margin: 20px auto;
   }
   
   @keyframes spin {
     0% { transform: rotate(0deg); }
     100% { transform: rotate(360deg); }
   }
   
   .error-message {
     color: #ef4444;
     background-color: #fee2e2;
     padding: 15px;
     border-radius: 8px;
     margin: 20px 0;
   }
   
   .btn-primary {
     margin-top: 1em;
   }

   @media (max-width: 768px) {
     .booking-success-view {
       padding: 40px 16px;
       align-items: flex-start;
     }

     .success-card {
       padding: 28px 20px;
     }

     .success-title {
       font-size: 1.75em;
     }

     .success-subtitle {
       font-size: 1rem;
     }

     .detail-item {
       flex-direction: column;
       gap: 4px;
       align-items: flex-start;
     }

     .detail-item ul {
       text-align: left;
     }
   }

   @media (max-width: 576px) {
     .success-icon {
       width: 64px;
       height: 64px;
       font-size: 2em;
     }

     .success-title {
       font-size: 1.5em;
     }
   }
</style>